import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { QUERY_CONFIG } from "../constants/QueryConfig";
import { useAuth } from "../contexts/AuthContext";
import { DocsRepository } from "../repositories/DocsRepository";
import { ChangelogRelease } from "../models/Docs";

/**
 * Docs routes exist both globally and under an organization, so a reader who opens help from an organization page
 * keeps that organization in the sidebar and portal links inside the docs can point at its screens.
 */
export const useDocsBasePath = (): string => {
  const { organizationId } = useParams<{ organizationId?: string }>();
  return organizationId ? `/organization/${organizationId}/docs` : "/docs";
};

export const useDocsManifest = () => {
  const repository = new DocsRepository();
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEYS.DOCS_MANIFEST],
    queryFn: () => repository.getManifest(),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    enabled: isAuthenticated,
    retry: 1,
  });
};

/**
 * Content changes only when a new server image is deployed, but a portal tab can stay open across that deploy, so
 * pages refresh on the portal's usual stale time rather than never.
 */
export const useDocsPage = (slug: string) => {
  const repository = new DocsRepository();
  return useQuery({
    queryKey: [QUERY_KEYS.DOCS_PAGE, slug],
    queryFn: () => repository.getPage(slug),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    retry: false,
  });
};

export const useDocsSearchIndex = (enabled: boolean) => {
  const repository = new DocsRepository();
  return useQuery({
    queryKey: [QUERY_KEYS.DOCS_SEARCH_INDEX],
    queryFn: () => repository.getSearchIndex(),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    enabled,
  });
};

export const useChangelog = (enabled = true) => {
  const repository = new DocsRepository();
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEYS.CHANGELOG],
    queryFn: () => repository.getChangelog(),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    enabled: isAuthenticated && enabled,
  });
};

export const useMarkReleaseNotesSeen = () => {
  const repository = new DocsRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (release: string) => repository.markReleaseNotesSeen(release),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCS_MANIFEST] }),
  });
};

const versionParts = (version: string): number[] =>
  version.split("+")[0].split(".").map((part) => Number(part));

const dayNumber = (year: number, month: number, day: number): number => year * 10000 + month * 100 + day;

/**
 * Finds the release that shipped a client or updater build. A build version is `<build day>.<build number>`, and the
 * build number is its tag's (`release-v17` builds `….17`). A release names its build before the prod pipeline runs,
 * so its build day is only a prediction: the match is on the build number, for a build made on or after the release
 * date. Dev builds carry numbers of their own (`dev-release-v…`), far ahead of prod's, so a dev build that shares a
 * prod build's number was made long before the release that names it.
 * Versions are compared number by number because the same build reads `2026.09.28.17` in blob and tag names and
 * `2026.9.28.17` when a client reports it. Builds with a `+suffix` are custom builds and never match a release.
 */
export const useReleaseForVersion = () => {
  const { data } = useChangelog();
  return (component: "client" | "updater", version?: string): ChangelogRelease | undefined => {
    if (!version || version.includes("+") || !data) return undefined;
    const wanted = versionParts(version);
    if (wanted.length !== 4 || wanted.some(Number.isNaN)) return undefined;
    const [year, month, day, number] = wanted;
    return data.releases.find((release) => {
      const build = release[component];
      if (!build) return false;
      const named = versionParts(build.version);
      const [releaseYear, releaseMonth, releaseDay] = release.date.split("-").map(Number);
      return named.length === 4 && named[3] === number
        && dayNumber(year, month, day) >= dayNumber(releaseYear, releaseMonth, releaseDay);
    });
  };
};
